import { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { WidgetContext } from "../widgetContext";

interface AnimatedNumberProps {
  value: number | string;
  formatter?: (value: number) => string | number;
  duration?: number;
  className?: string;
}

/**
 * AnimatedNumber component that animates numeric value changes.
 * Animation is only enabled when enableValueAnimation is true in WidgetContext.
 * Works for both real-time Pusher updates and filter-driven data changes.
 */
const AnimatedNumber = ({
  value,
  formatter,
  duration = 400,
  className = "",
}: AnimatedNumberProps) => {
  const { enableValueAnimation } = useContext(WidgetContext);
  const [displayValue, setDisplayValue] = useState<string | number>(
    formatter ? formatter(Number(value)) : value
  );
  const [isAnimating, setIsAnimating] = useState(false);
  const previousValueRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const numericValue = Number(value);
    const formattedValue = formatter ? formatter(numericValue) : value;

    // Skip animation on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousValueRef.current = numericValue;
      setDisplayValue(formattedValue);
      return;
    }

    // If animation is disabled, just update the value directly
    if (!enableValueAnimation) {
      previousValueRef.current = numericValue;
      setDisplayValue(formattedValue);
      return;
    }

    const previousValue = previousValueRef.current;

    // No change in value, skip animation
    if (previousValue === numericValue) {
      return;
    }

    // Cancel any ongoing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Trigger highlight animation
    setIsAnimating(true);

    // Animate the number count
    const startValue = previousValue ?? numericValue;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic for smooth deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3);

      const currentValue = startValue + (numericValue - startValue) * easeOut;
      const roundedValue = Math.round(currentValue);

      setDisplayValue(formatter ? formatter(roundedValue) : roundedValue);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Ensure final value is exact
        setDisplayValue(formattedValue);
        previousValueRef.current = numericValue;

        // Remove highlight after animation completes
        setTimeout(() => {
          setIsAnimating(false);
        }, 150);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    // Cleanup on unmount
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [value, formatter, duration, enableValueAnimation]);

  // Update previous value ref when animation is disabled
  useEffect(() => {
    if (!enableValueAnimation) {
      previousValueRef.current = Number(value);
    }
  }, [value, enableValueAnimation]);

  return (
    <AnimatedSpan className={`${className} ${isAnimating ? "animating" : ""}`}>
      {displayValue}
    </AnimatedSpan>
  );
};

const AnimatedSpan = styled.span`
  display: inline-block;
  transition: transform 0.15s ease-out, color 0.15s ease-out;

  &.animating {
    transform: scale(1.05);
    color: #0d8a6a;
  }
`;

export default AnimatedNumber;
